/**
 * Custom Font Loading Detection
 * Replaces FontFaceObserver for local fonts
 */

class CustomFontLoader {
    constructor(fontFamily, options = {}) {
        this.fontFamily = fontFamily;
        this.options = {
            timeout: 3000, // 3 second timeout
            ...options
        };
    }

    load() {
        return new Promise((resolve, reject) => {
            // If document.fonts API is available, use it
            if (document.fonts && document.fonts.load) {
                this.loadWithFontsAPI(resolve, reject);
            } else {
                // Fallback to canvas measurement method
                this.loadWithCanvasMethod(resolve, reject);
            }
        });
    }

    loadWithFontsAPI(resolve, reject) {
        const timeout = setTimeout(() => {
            reject(new Error(`Font ${this.fontFamily} failed to load within ${this.options.timeout}ms`));
        }, this.options.timeout);

        document.fonts.load(`16px "${this.fontFamily}"`).then(() => {
            clearTimeout(timeout);
            resolve();
        }).catch(() => {
            clearTimeout(timeout);
            reject(new Error(`Font ${this.fontFamily} failed to load`));
        });
    }

    loadWithCanvasMethod(resolve, reject) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        // Test string to measure
        const testString = 'abcdefghijklmnopqrstuvwxyz0123456789';
        const fontSize = '72px';
        
        // Measure with fallback font
        context.font = `${fontSize} Arial, sans-serif`;
        const fallbackWidth = context.measureText(testString).width;
        
        // Measure with target font
        context.font = `${fontSize} "${this.fontFamily}", Arial, sans-serif`;
        const targetWidth = context.measureText(testString).width;
        
        // If widths are different, font is loaded
        if (Math.abs(targetWidth - fallbackWidth) > 1) {
            resolve();
            return;
        }
        
        // Font not loaded yet, poll for changes
        let attempts = 0;
        const maxAttempts = this.options.timeout / 50; // Check every 50ms
        
        const checkFont = () => {
            attempts++;
            
            context.font = `${fontSize} "${this.fontFamily}", Arial, sans-serif`;
            const currentWidth = context.measureText(testString).width;
            
            if (Math.abs(currentWidth - fallbackWidth) > 1) {
                resolve();
            } else if (attempts >= maxAttempts) {
                reject(new Error(`Font ${this.fontFamily} failed to load within ${this.options.timeout}ms`));
            } else {
                setTimeout(checkFont, 50);
            }
        };
        
        checkFont();
    }
}

// Export for use in modules
export default CustomFontLoader;

// Also make available globally for compatibility
window.CustomFontLoader = CustomFontLoader;
